import { useState } from 'react';
import type { VoidFunctionComponent } from 'react';

import { window } from '../../../../globals';
import Button from '../../../generic/Button';
import Icon, { icons } from '../../../generic/Icon';
import List from '../../../generic/List';

import ItemizedRule from './ItemizedRule';
import ItemizedRuleListInput from './ItemizedRuleListInput';
import type { Suggestion } from './types';

import styles from './ItemizedRuleList.module.less';

type Props = {
  inputDecoration: string;
  rules: string[];
  onRulesChange: (rules: string[]) => void;
  getTitle?: (id: string) => string | undefined;
  buildLink?: (id: string) => string;
  getSuggestions: (term: string) => Promise<Suggestion[]>;
  rulePrefix?: string;
  isLoading?: boolean;
  isDisabled?: boolean;
};

const ItemizedRuleList: VoidFunctionComponent<Props> = ({
  inputDecoration,
  rules,
  onRulesChange,
  getTitle,
  buildLink,
  getSuggestions,
  rulePrefix,
  isLoading = false,
  isDisabled = false,
}) => {
  const [isAdding, setIsAdding] = useState(false);

  const addRule = (id: string | null) => {
    if (id && !rules.includes(id)) {
      onRulesChange([...rules, id]);
    }
    setIsAdding(false);
  };

  const removeRule = (id: string) => {
    onRulesChange(rules.filter((rule) => rule !== id));
  };

  const renderBody = () => {
    if (isLoading && rules.length > 0) {
      return rules.map((rule) => (
        <List.RowLoading key={rule} isInteractive={false} />
      ));
    }

    if (rules.length === 0 && !isAdding) {
      return (
        <List.Row isInteractive={false}>
          <List.RowTitle>No rules to display.</List.RowTitle>
        </List.Row>
      );
    }

    return (
      <>
        {rules.map((rule) => (
          <List.RowContainer key={rule}>
            <List.Row
              isInteractive={false}
              right={
                <div className={styles.rowActions}>
                  {buildLink && (
                    <Button
                      size="small"
                      type="secondary"
                      onClick={() => window.open(buildLink(rule), '_blank', 'noopener')}
                    >
                      View <Icon icon={icons.externalLink} />
                    </Button>
                  )}
                  <Button size="small" type="error" onClick={() => removeRule(rule)}>
                    Remove
                  </Button>
                </div>
              }
            >
              <ItemizedRule id={rule} title={getTitle?.(rule)} prefix={rulePrefix} />
            </List.Row>
          </List.RowContainer>
        ))}
        {isAdding && (
          <List.RowContainer>
            <List.Row
              isInteractive={false}
              right={
                <Button size="small" type="error" onClick={() => setIsAdding(false)}>
                  Cancel
                </Button>
              }
            >
              <ItemizedRuleListInput
                decoration={inputDecoration}
                addRule={addRule}
                getSuggestions={getSuggestions}
              />
            </List.Row>
          </List.RowContainer>
        )}
      </>
    );
  };

  return (
    <List>
      <List.Header>
        <List.HeaderText>Rules</List.HeaderText>
        <Button size="small" type="secondary" onClick={() => setIsAdding(true)} isDisabled={isAdding || isDisabled}>
          Add Rule
        </Button>
      </List.Header>
      <List.Body>
        {renderBody()}
      </List.Body>
    </List>
  );
};

export default ItemizedRuleList;
